import { array, boolean, mixed, object, type Schema } from 'yup';

import { useUpdateRecordSource } from '@hooks/api/curation-meta';
import { useUploadFile } from '@hooks/api/file';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useMultiUploadProgress } from '@hooks/useMultiUploadProgress';

import type { BaseFhirFormValues } from '@models/fhir-resource';

import { useUpdateCurationStatusMutation } from 'generated/curation-meta.generated';
import { CurationStatus, type ResourceSourceType } from 'generated/types';

import { useCuration } from './useCuration';
import { useSession } from './useSession';

const useFhirRecordForm = () => {
  const { recordSourceSchema } = useCommonSchemas();
  const [{ uploadAttachment }] = useUploadFile();
  const {
    state: { record },
  } = useCuration();
  const initialRecordSource =
    record?.curationMeta?.source_type ?? record?.recordSource;
  const {
    state: { isCurator },
  } = useSession();
  const {
    progress,
    setNumFiles,
    setUploadProgress,
    setUploadedFiles,
    setChunkSize,
    reset,
  } = useMultiUploadProgress();

  const [updateRecordSource, updateRecordSourceLoading] =
    useUpdateRecordSource();
  const [updateCurationStatus, { loading: updateCurationStatusLoading }] =
    useUpdateCurationStatusMutation();

  const loading =
    updateRecordSourceLoading ||
    updateCurationStatusLoading ||
    typeof progress === 'number';

  const initialFhirFormValues: BaseFhirFormValues = {
    submit: null,
    toPublish: false,
    files: [],
    recordSource: initialRecordSource,
  };

  const baseFhirValidationSchema: Schema<Partial<BaseFhirFormValues>> =
    object().shape({
      toPublish: boolean(),
      recordSource: recordSourceSchema,
      files: array().of(mixed<DroppedFile>().required()),
    });

  /**
   * Publish a record
   * @param resource FHIR resource in format <resourceType>/<resourceId>
   */
  const publishResource = async (resource: string) => {
    await updateCurationStatus({
      variables: {
        input: {
          resource,
          status: CurationStatus.Published,
        },
      },
    });
  };

  /**
   * Add or update a record's source
   * @param resource FHIR resource in format <resourceType>/<resourceId>
   * @param source_type Where record came from (ResourceSourceType)
   */
  const upsertRecordSource = async (
    resource: string,
    source_type: ResourceSourceType
  ) => {
    await updateRecordSource({
      input: {
        resource,
        source_type,
      },
    });
  };

  /**
   * Attach file(s) to a FHIR record
   * @param resource FHIR resource in format <resourceType>/<resourceId>
   * @param files Files to attach
   */
  const uploadFiles = async (resource: string, files: DroppedFile[]) => {
    if (!files.length) {
      return;
    }
    setNumFiles(files.length);
    setChunkSize(1);
    for (const file of files) {
      await uploadAttachment(resource, file, (progress) =>
        setUploadProgress(progress)
      );
      setUploadedFiles((prev) => prev + 1);
      setUploadProgress(0);
    }
    reset();
  };

  /**
   * 1. If 'Save & Publish' btn is clicked, saves and publishes a record
   * 2. If files are added, uploads and attaches files to a record
   * 3. If a record's source has changed, updates it
   * @param param0 User's input (publish, files, record source)
   */
  const handleRecordFilesSourcePublishing = async ({
    files,
    toPublish,
    recordSource,
    resource,
  }: {
    files?: DroppedFile[];
    toPublish?: boolean | null;
    recordSource?: ResourceSourceType;
    resource?: string;
  }) => {
    if (!resource) {
      return;
    }
    if (files && files.length > 0) {
      await uploadFiles(resource, files);
    }
    if (isCurator && toPublish) {
      await publishResource(resource);
    }
    if (recordSource) {
      await upsertRecordSource(resource, recordSource);
    }
  };

  return {
    formikHelpers: {
      initialFhirFormValues,
      baseFhirValidationSchema,
    },
    loading,
    handleRecordFilesSourcePublishing,
    publishResource,
    uploadFiles,
    upsertRecordSource,
  };
};

export default useFhirRecordForm;
